// @ts-nocheck -- verbatim TAPP payroll UI compatibility boundary
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cycleDialogFocus } from "./focusCycle.js";
import { Button } from "../ui/Button.js";
import {
  type QueryClient,
  type QueryKey,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  CircleAlert,
  Clock3,
  LoaderCircle,
  RefreshCw,
  RotateCcw,
  X,
} from "lucide-react";
import type {
  OpenProjectPayrollValueCorrectionInspection,
  OpenProjectPayrollValueCorrectionMutation,
  OpenProjectPayrollValueCorrectionMutationResponse,
} from "../../types/openproject";

import { api, PayrollApiError } from "../../lib/api";
import type { PayrollDisplayUnit } from "../../routes/payrollDisplay";
import {
  type ValueCorrectionFieldKey,
  valueCorrectionField,
} from "./valueCorrectionFields";
import { PayrollAuditLinks } from "./PayrollAuditLinks";

type CorrectionOperation =
  OpenProjectPayrollValueCorrectionMutation["operation"];
type ReasonCategory =
  OpenProjectPayrollValueCorrectionMutation["reasonCategory"];

export type ValueCorrectionTarget = {
  userId: number;
  employeeName: string;
  month: string;
  periodThrough: string;
  fieldKey: ValueCorrectionFieldKey;
  displayUnit: PayrollDisplayUnit;
  hoursPerDay: number | null;
  trigger: HTMLButtonElement | null;
};

type ValueCorrectionInspectorProps = {
  target: ValueCorrectionTarget;
  onClose: () => void;
};

export const valueCorrectionQueryKey = (
  userId: number,
  month: string,
  fieldKey: ValueCorrectionFieldKey,
) =>
  ["openproject", "payroll-value-correction", userId, month, fieldKey] as const;

export const REASON_CATEGORY_SUGGESTIONS: ReadonlyArray<{
  value: ReasonCategory;
  label: string;
}> = [
  { value: "source_error", label: "Source document correction" },
  { value: "payroll_document_error", label: "Missing source value" },
  { value: "calculation_error", label: "Payroll reconciliation" },
  { value: "approved_exception", label: "Carry-forward correction" },
  { value: "other", label: "Other" },
];

const reasonLabel = (category: ReasonCategory) =>
  REASON_CATEGORY_SUGGESTIONS.find((item) => item.value === category)?.label ??
  (category === "rounding" ? "Rounding correction" : category);

const POHODA_INVALIDATING_FIELDS = new Set<ValueCorrectionFieldKey>([
  "timesheet.total",
  "vacation.timesheet",
  "doctor.timesheet",
  "family_care.timesheet",
  "illness.timesheet",
  "public_holiday.timesheet",
  "paid_leave.timesheet",
]);

// The standalone control API exposes only the safe field key and label. Keep
// the presentation and client-side validation metadata local and keyed by the
// same closed field registry used by the payroll service.
export const valueCorrectionInspectorField = (
  key: ValueCorrectionFieldKey,
) => ({
  ...valueCorrectionField(key),
  invalidationStage: POHODA_INVALIDATING_FIELDS.has(key)
    ? ("pohoda" as const)
    : ("reconcile" as const),
  minimumHours: key === "overtime.calculated" ? -10_000 : 0,
  maximumHours: 10_000,
});

const displayMonth = (month: string) => {
  const [year, monthNumber] = month.split("-").map(Number);
  if (!year || !monthNumber) return month;
  return new Intl.DateTimeFormat(undefined, {
    month: "long",
    year: "numeric",
  }).format(new Date(year, monthNumber - 1, 1));
};

const displayTimestamp = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const roundHours = (value: number) =>
  Math.round((value + Number.EPSILON) * 100) / 100;

export const correctionDisplayValueToHours = (
  value: number,
  unit: PayrollDisplayUnit,
  hoursPerDay: number | null,
) =>
  roundHours(
    unit === "days" && typeof hoursPerDay === "number"
      ? value * hoursPerDay
      : value,
  );

export const correctionDraftValueHours = ({
  value,
  unit,
  hoursPerDay,
  canonicalValueHours,
  valueEdited,
}: {
  value: string;
  unit: PayrollDisplayUnit;
  hoursPerDay: number | null;
  canonicalValueHours: number | null;
  valueEdited: boolean;
}) =>
  !valueEdited && canonicalValueHours !== null
    ? canonicalValueHours
    : correctionDisplayValueToHours(Number(value), unit, hoursPerDay);

const hoursToDisplayValue = (
  value: number | null,
  unit: PayrollDisplayUnit,
  hoursPerDay: number | null,
) => {
  if (value === null) return "";
  if (unit === "days" && typeof hoursPerDay === "number")
    return String(roundHours(value / hoursPerDay));
  return String(value);
};

export const validateCorrectionDraft = ({
  operation,
  value,
  note,
  unit,
  hoursPerDay,
  minimumHours,
  maximumHours,
  canonicalValueHours = null,
  valueEdited = true,
}: {
  operation: CorrectionOperation;
  value: string;
  note: string;
  unit: PayrollDisplayUnit;
  hoursPerDay: number | null;
  minimumHours: number;
  maximumHours: number;
  canonicalValueHours?: number | null;
  valueEdited?: boolean;
}) => {
  const trimmedNote = note.trim();
  if (trimmedNote.length < 3 || trimmedNote.length > 500)
    return "Note must be between 3 and 500 characters.";
  if (operation !== "set") return null;
  if (value.trim() === "") return "Enter a corrected value.";
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) return "Enter a valid number.";
  if (unit === "days" && (!hoursPerDay || hoursPerDay <= 0))
    return "Hours per day are missing, so this value cannot be entered in days.";
  const valueHours = correctionDraftValueHours({
    value,
    unit,
    hoursPerDay,
    canonicalValueHours,
    valueEdited,
  });
  if (valueHours < minimumHours || valueHours > maximumHours)
    return `Value must be between ${minimumHours.toFixed(2)} and ${maximumHours.toFixed(2)} hours.`;
  return null;
};

export const claimValueInspectorEscape = (
  event: Pick<
    KeyboardEvent,
    "key" | "preventDefault" | "stopPropagation" | "stopImmediatePropagation"
  >,
  close: (event: InspectorCloseEvent) => void,
) => {
  if (event.key !== "Escape") return false;
  close(event);
  return true;
};

type InspectorCloseEvent = Pick<Event, "preventDefault" | "stopPropagation"> & {
  stopImmediatePropagation?: () => void;
  nativeEvent?: {
    stopImmediatePropagation?: () => void;
  };
};

export const shieldValueInspectorCloseEvent = (
  event: InspectorCloseEvent | null | undefined,
) => {
  if (!event) return;
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation?.();
  event.nativeEvent?.stopImmediatePropagation?.();
};

export const restoreValueCorrectionFocus = (
  trigger: Pick<HTMLElement, "focus"> | null,
  schedule: (callback: () => void) => void = (callback) =>
    window.requestAnimationFrame(callback),
) => {
  schedule(() => trigger?.focus());
};

export const valueCorrectionPortalTarget = (
  trigger: HTMLElement | null,
): HTMLElement | null => {
  const ownerDocument =
    trigger?.ownerDocument ??
    (typeof document === "undefined" ? null : document);
  if (!ownerDocument) return null;

  const modalDialog =
    trigger?.closest<HTMLElement>(
      ".period-detail-modal, .tapp-modal__dialog, [role='dialog']",
    ) ?? null;
  return modalDialog?.isConnected ? modalDialog : ownerDocument.body;
};

export const correctionRecalculationHeading = (
  state: OpenProjectPayrollValueCorrectionMutationResponse["recalculation"]["state"],
) =>
  state === "complete"
    ? "Calculations refreshed"
    : state === "pending"
      ? "Correction recorded; calculations pending"
      : "Correction recorded; recalculation failed";

const operationLabel = (operation: CorrectionOperation) => {
  if (operation === "revert") return "Correction reverted";
  if (operation === "reaffirm") return "Correction kept after source change";
  return "Correction set";
};

const selectedUnitValueLabel = (
  valueHours: number | null,
  unit: PayrollDisplayUnit,
  hoursPerDay: number | null,
) => {
  if (valueHours === null) return "Missing";
  const displayValue =
    unit === "days"
      ? valueHours /
        (typeof hoursPerDay === "number" && hoursPerDay > 0 ? hoursPerDay : 8)
      : valueHours;
  return `${displayValue.toFixed(2)} ${unit === "days" ? "d" : "h"}`;
};

const canonicalHoursEvidenceLabel = (value: number | null) =>
  value === null ? "Missing" : `${value.toFixed(2)} h`;

export const ValueCorrectionTimeline = ({
  events,
  displayUnit,
  hoursPerDay,
}: {
  events: OpenProjectPayrollValueCorrectionInspection["events"];
  displayUnit: PayrollDisplayUnit;
  hoursPerDay: number | null;
}) =>
  events.length === 0 ? (
    <div className="value-inspector__empty">
      <Clock3 size={18} aria-hidden="true" />
      <p>No manual correction has been recorded for this value.</p>
    </div>
  ) : (
    <ol className="value-inspector__timeline">
      {events.map((event) => (
        <li key={event.id}>
          <span className="value-inspector__timeline-marker" />
          <div>
            <div className="value-inspector__timeline-title">
              <h4>{operationLabel(event.operation)}</h4>
              <span>r{event.revision}</span>
            </div>
            <p className="value-inspector__timeline-change">
              {selectedUnitValueLabel(
                event.beforeValueHours,
                displayUnit,
                hoursPerDay,
              )}{" "}
              →{" "}
              {selectedUnitValueLabel(
                event.afterValueHours,
                displayUnit,
                hoursPerDay,
              )}
            </p>
            <p className="value-inspector__timeline-note">{event.note}</p>
            <p className="value-inspector__timeline-meta">
              {reasonLabel(event.reasonCategory)} · {event.actorName} ·{" "}
              <time dateTime={event.occurredAt}>
                {displayTimestamp(event.occurredAt)}
              </time>
            </p>
            <details className="value-inspector__ledger-evidence">
              <summary
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key !== "Enter" && event.key !== " ") return;
                  event.preventDefault();
                  const details = event.currentTarget
                    .parentElement as HTMLDetailsElement;
                  details.open = !details.open;
                }}
              >
                Ledger evidence
              </summary>
              <dl>
                <div>
                  <dt>Original (canonical hours)</dt>
                  <dd>
                    {canonicalHoursEvidenceLabel(event.originalValueHours)}
                  </dd>
                </div>
                <div>
                  <dt>Event</dt>
                  <dd>#{event.id}</dd>
                </div>
                <div>
                  <dt>Request</dt>
                  <dd>{event.requestId}</dd>
                </div>
                <div>
                  <dt>Source fingerprint</dt>
                  <dd>{event.sourceFingerprint}</dd>
                </div>
              </dl>
            </details>
          </div>
        </li>
      ))}
    </ol>
  );

const invalidationMonths = (
  response: OpenProjectPayrollValueCorrectionMutationResponse,
) =>
  [
    ...new Set([
      response.inspection.month,
      ...response.mutation.affectedMonths,
      ...response.recalculation.affectedMonths,
    ]),
  ].sort();

export const correctionInvalidationQueryKeys = (
  response: OpenProjectPayrollValueCorrectionMutationResponse,
): QueryKey[] => {
  const months = invalidationMonths(response);
  const invalidatesPohoda = ["prepare", "pohoda"].includes(
    valueCorrectionInspectorField(response.inspection.field.key)
      .invalidationStage,
  );
  return [
    ["openproject", "balances", "period-summary"],
    ...months.flatMap((month) => [
      ["openproject", "balances", month],
      ["openproject", "payroll-workflow", month],
      [
        "openproject",
        "payroll-value-correction",
        response.inspection.employeeId,
        month,
      ],
      ...(invalidatesPohoda
        ? [
            ["openproject", "pohoda-preview", month],
            ["openproject", "pohoda-exports", month],
          ]
        : []),
    ]),
    ["openproject", "payroll-audit"],
  ];
};

export const invalidateCorrectionDependencies = async (
  client: QueryClient,
  response: OpenProjectPayrollValueCorrectionMutationResponse,
) =>
  Promise.all(
    correctionInvalidationQueryKeys(response).map((queryKey) =>
      client.invalidateQueries({ queryKey }),
    ),
  );

export const ValueCorrectionInspector = ({
  target,
  onClose,
}: ValueCorrectionInspectorProps) => {
  const headingId = useId();
  const descriptionId = useId();
  const conflictHeadingId = useId();
  const client = useQueryClient();
  const drawerRef = useRef<HTMLElement>(null);
  const valueInputRef = useRef<HTMLInputElement>(null);
  const exceptionalAcknowledgementRef = useRef<HTMLInputElement>(null);
  const closeRequestedRef = useRef(false);
  const [operation, setOperation] = useState<CorrectionOperation>("set");
  const [value, setValue] = useState("");
  const [valueEdited, setValueEdited] = useState(false);
  const [reasonCategory, setReasonCategory] =
    useState<ReasonCategory>("source_error");
  const [note, setNote] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  const [exceptionalMode, setExceptionalMode] = useState(false);
  const [exceptionalAcknowledged, setExceptionalAcknowledged] = useState(false);
  const [recalculation, setRecalculation] = useState<
    OpenProjectPayrollValueCorrectionMutationResponse["recalculation"] | null
  >(null);

  const queryKey = valueCorrectionQueryKey(
    target.userId,
    target.month,
    target.fieldKey,
  );
  const inspectionQuery = useQuery({
    queryKey,
    queryFn: () =>
      api.getOpenProjectPayrollValueCorrection(
        target.userId,
        target.month,
        target.fieldKey,
      ),
    retry: false,
  });
  const inspection = inspectionQuery.data;

  const close = (event?: InspectorCloseEvent) => {
    shieldValueInspectorCloseEvent(event);
    if (closeRequestedRef.current) return;
    closeRequestedRef.current = true;
    onClose();
    restoreValueCorrectionFocus(target.trigger);
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (new URLSearchParams(window.location.search).has("auditOverlay"))
        return;
      if (claimValueInspectorEscape(event, close)) return;
      cycleDialogFocus(event, drawerRef.current);
    };
    document.addEventListener("keydown", onKeyDown, true);
    window.requestAnimationFrame(() => {
      drawerRef.current
        ?.querySelector<HTMLElement>("[data-inspector-initial-focus]")
        ?.focus();
    });
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [target.fieldKey, target.month, target.trigger]);

  useEffect(() => {
    if (!inspection) return;
    setValue(
      hoursToDisplayValue(
        inspection.effectiveValueHours ?? inspection.originalValueHours,
        target.displayUnit,
        target.hoursPerDay,
      ),
    );
    setValueEdited(false);
    const activeCategory = inspection.activeCorrection?.reasonCategory;
    setReasonCategory(
      REASON_CATEGORY_SUGGESTIONS.some(
        (suggestion) => suggestion.value === activeCategory,
      )
        ? (activeCategory as ReasonCategory)
        : "source_error",
    );
    setNote("");
    setOperation(
      inspection.activeCorrection && inspection.sourceReview.reviewRequired
        ? "reaffirm"
        : "set",
    );
    setFormError(null);
  }, [
    inspection,
    target.displayUnit,
    target.fieldKey,
    target.hoursPerDay,
    target.month,
  ]);

  useEffect(() => {
    setRecalculation(null);
    setConflict(null);
    setExceptionalMode(false);
    setExceptionalAcknowledged(false);
    closeRequestedRef.current = false;
  }, [target.fieldKey, target.month, target.userId]);

  const correctionMutation = useMutation({
    mutationFn: (payload: OpenProjectPayrollValueCorrectionMutation) =>
      api.mutateOpenProjectPayrollValueCorrection(
        target.userId,
        target.month,
        target.fieldKey,
        payload,
      ),
    onSuccess: async (response) => {
      setConflict(null);
      setRecalculation(response.recalculation);
      client.setQueryData(queryKey, response.inspection);
      await invalidateCorrectionDependencies(client, response);
    },
    onError: (error) => {
      if (error instanceof PayrollApiError && error.status === 409) {
        setConflict(
          error.message ||
            "This value changed in another session. Reload it before continuing.",
        );
      }
    },
  });

  const fieldDefinition = valueCorrectionInspectorField(target.fieldKey);
  const displayUnitLabel = target.displayUnit === "days" ? "days" : "hours";
  const allowedSuggestions = useMemo(() => {
    if (!inspection) return REASON_CATEGORY_SUGGESTIONS;
    return REASON_CATEGORY_SUGGESTIONS.filter((suggestion) =>
      inspection.allowedReasonCategories.includes(suggestion.value),
    );
  }, [inspection]);

  const submit = () => {
    if (!inspection || conflict) return;
    if (exceptionalMode && !exceptionalAcknowledged) {
      setFormError(
        "Acknowledge the closed-payroll consequences before continuing.",
      );
      return;
    }
    const canonicalValueHours =
      inspection.effectiveValueHours ?? inspection.originalValueHours;
    const validationError = validateCorrectionDraft({
      operation,
      value,
      note,
      unit: target.displayUnit,
      hoursPerDay: target.hoursPerDay,
      minimumHours: fieldDefinition.minimumHours,
      maximumHours: fieldDefinition.maximumHours,
      canonicalValueHours,
      valueEdited,
    });
    setFormError(validationError);
    if (validationError) return;

    const payload: OpenProjectPayrollValueCorrectionMutation = {
      requestId: `correction:${Date.now()}:${crypto.randomUUID()}`,
      operation,
      expectedRevision: inspection.revision,
      sourceFingerprint: inspection.sourceFingerprint,
      reasonCategory,
      note: note.trim(),
      ...(exceptionalMode
        ? {
            exceptionalAccess: {
              acknowledged: true as const,
              warningVersion:
                inspection.editEligibility.exceptionalAccess.warningVersion,
            },
          }
        : {}),
      ...(operation === "set"
        ? {
            valueHours: correctionDraftValueHours({
              value,
              unit: target.displayUnit,
              hoursPerDay: target.hoursPerDay,
              canonicalValueHours,
              valueEdited,
            }),
          }
        : {}),
    };
    correctionMutation.mutate(payload);
  };

  const chooseOperation = (nextOperation: CorrectionOperation) => {
    setOperation(nextOperation);
    setFormError(null);
    if (nextOperation === "set")
      window.requestAnimationFrame(() => valueInputRef.current?.focus());
  };

  const reloadAfterConflict = async () => {
    const result = await inspectionQuery.refetch();
    if (!result.isSuccess) return;
    correctionMutation.reset();
    setConflict(null);
  };

  const requiresExceptionalMode = Boolean(
    inspection?.editEligibility.exceptionalAccess.required,
  );
  const exceptionalModeAvailable = Boolean(
    requiresExceptionalMode &&
    inspection?.editEligibility.canMutate &&
    inspection.editEligibility.exceptionalAccess.available &&
    inspection.editEligibility.exceptionalAccess.authorized,
  );
  const exceptionalModeUnlocked =
    exceptionalModeAvailable && exceptionalMode && exceptionalAcknowledged;
  const isBlocked = requiresExceptionalMode && !exceptionalModeUnlocked;
  const isMutationBlocked = isBlocked || Boolean(conflict);
  const isSourceChanged = Boolean(
    inspection?.activeCorrection && inspection.sourceReview.reviewRequired,
  );
  const canRevert = Boolean(inspection?.activeCorrection);
  const operationOptions: Array<{
    operation: CorrectionOperation;
    label: string;
  }> = isSourceChanged
    ? [
        { operation: "reaffirm", label: "Keep correction" },
        { operation: "set", label: "Revise" },
        { operation: "revert", label: "Revert" },
      ]
    : canRevert
      ? [
          { operation: "set", label: "Revise" },
          { operation: "revert", label: "Revert" },
        ]
      : [{ operation: "set", label: "Set correction" }];

  const primaryLabel =
    operation === "reaffirm"
      ? "Keep correction"
      : operation === "revert"
        ? "Revert correction"
        : inspection?.activeCorrection
          ? "Save revision"
          : "Set correction";

  const result = (
    <div
      className="value-inspector__backdrop"
      onPointerDown={(event) => {
        event.stopPropagation();
        if (event.currentTarget === event.target) close(event);
      }}
      onClick={(event) => event.stopPropagation()}
    >
      <aside
        ref={drawerRef}
        className="value-inspector"
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        aria-describedby={descriptionId}
      >
        <div className="value-inspector__scroll">
          <header className="value-inspector__header">
            <div className="min-w-0">
              <p className="value-inspector__eyebrow">Value details</p>
              <h2 id={headingId}>{fieldDefinition.metric}</h2>
              <p id={descriptionId}>
                {target.employeeName} · {displayMonth(target.month)}
              </p>
            </div>
            <Button variant="ghost"
              type="button"
              className="value-inspector__close"
              aria-label="Close value details"
              data-inspector-initial-focus
              onClick={close}
            >
              <X size={18} aria-hidden="true" />
            </Button>
          </header>

          {inspectionQuery.isLoading && (
            <div
              className="value-inspector__loading"
              role="status"
              aria-live="polite"
            >
              <LoaderCircle className="animate-spin" size={18} />
              Loading the exact value and manual history…
            </div>
          )}

          {inspectionQuery.isError && !inspection && (
            <section className="value-inspector__state value-inspector__state--error">
              <CircleAlert size={18} aria-hidden="true" />
              <div>
                <h3>Value details could not be loaded</h3>
                <p>
                  {inspectionQuery.error instanceof Error
                    ? inspectionQuery.error.message
                    : "The correction ledger is unavailable."}
                </p>
                <Button variant="ghost" type="button" onClick={() => inspectionQuery.refetch()}>
                  <RefreshCw size={14} aria-hidden="true" />
                  Try again
                </Button>
              </div>
            </section>
          )}

          {inspection && (
            <>
              <section className="value-inspector__current">
                <div className="value-inspector__current-heading">
                  <div>
                    <p>Current value</p>
                    <strong>
                      {inspection.effectiveValueHours === null
                        ? "Missing"
                        : selectedUnitValueLabel(
                            inspection.effectiveValueHours,
                            target.displayUnit,
                            target.hoursPerDay,
                          )}
                    </strong>
                  </div>
                  <span className="value-inspector__source">
                    {fieldDefinition.source}
                  </span>
                </div>
                <dl className="value-inspector__facts">
                  <div>
                    <dt>Original</dt>
                    <dd>
                      {selectedUnitValueLabel(
                        inspection.originalValueHours,
                        target.displayUnit,
                        target.hoursPerDay,
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt>Effective</dt>
                    <dd>
                      {selectedUnitValueLabel(
                        inspection.effectiveValueHours,
                        target.displayUnit,
                        target.hoursPerDay,
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt>Status</dt>
                    <dd>
                      {isSourceChanged
                        ? "Source changed"
                        : inspection.activeCorrection
                          ? "Corrected"
                          : "Original"}
                    </dd>
                  </div>
                </dl>
                <PayrollAuditLinks
                  compact
                  context={{
                    month: target.month,
                    periodThrough: target.periodThrough,
                    employeeId: target.userId,
                    userId: target.userId,
                    fieldKey: target.fieldKey,
                  }}
                />
                {inspection.originalValueHours === null && (
                  <div className="value-inspector__notice value-inspector__notice--warning">
                    <AlertTriangle size={16} aria-hidden="true" />
                    <p>
                      The original source value is missing. You can still set a
                      correction while the affected months are open.
                    </p>
                  </div>
                )}
                {isSourceChanged && (
                  <div className="value-inspector__notice value-inspector__notice--warning">
                    <AlertTriangle size={16} aria-hidden="true" />
                    <p>
                      The source changed after this correction was reviewed.
                      Keep it, revise it against the new source, or revert it.
                    </p>
                  </div>
                )}
              </section>

              {requiresExceptionalMode && !exceptionalMode && (
                <section className="value-inspector__blocked" role="status">
                  <CircleAlert size={18} aria-hidden="true" />
                  <div>
                    <h3>
                      {exceptionalModeAvailable
                        ? "Closed payroll is read-only"
                        : "This value is read-only"}
                    </h3>
                    <p>
                      Edits are disabled because the correction affects{" "}
                      {inspection.editEligibility.blockingMonths
                        .map(displayMonth)
                        .join(", ")}
                      .
                    </p>
                    {fieldDefinition.source === "payslip" &&
                      !inspection.editEligibility.canMutate && (
                        <p>
                          Reopen the affected payroll months before changing this payslip value.
                        </p>
                      )}
                    {exceptionalModeAvailable && (
                      <Button variant="ghost"
                        type="button"
                        onClick={() => {
                          setExceptionalMode(true);
                          setExceptionalAcknowledged(false);
                          setFormError(null);
                          window.requestAnimationFrame(() =>
                            exceptionalAcknowledgementRef.current?.focus(),
                          );
                        }}
                      >
                        Enter Exceptional correction mode
                      </Button>
                    )}
                  </div>
                </section>
              )}

              {requiresExceptionalMode && exceptionalMode && (
                <section
                  className="value-inspector__exceptional"
                  aria-labelledby={`${headingId}-exceptional`}
                >
                  <div className="value-inspector__exceptional-heading">
                    <AlertTriangle size={18} aria-hidden="true" />
                    <div>
                      <h3 id={`${headingId}-exceptional`}>
                        Exceptional correction mode
                      </h3>
                      <p>
                        Changing a closed payroll makes its evidence, approvals,
                        calculations, and exports stale. They must be reviewed
                        and regenerated before this period is relied on again.
                      </p>
                    </div>
                  </div>
                  <label>
                    <input
                      ref={exceptionalAcknowledgementRef}
                      type="checkbox"
                      checked={exceptionalAcknowledged}
                      onChange={(event) => {
                        setExceptionalAcknowledged(event.target.checked);
                        setFormError(null);
                      }}
                    />
                    <span>
                      I understand that this request will stale closed-payroll
                      evidence and downstream approvals.
                    </span>
                  </label>
                  <p className="value-inspector__exceptional-authority">
                    This mode does not grant access. The payroll server checks
                    your capability again when you submit.
                  </p>
                  <Button variant="ghost"
                    type="button"
                    className="value-inspector__exceptional-exit"
                    onClick={() => {
                      setExceptionalMode(false);
                      setExceptionalAcknowledged(false);
                      setFormError(null);
                    }}
                  >
                    Exit to read-only details
                  </Button>
                </section>
              )}

              <section
                className="value-inspector__edit"
                aria-labelledby={`${headingId}-edit`}
                aria-disabled={isBlocked}
              >
                <div className="value-inspector__section-heading">
                  <div>
                    <h3 id={`${headingId}-edit`}>Manual correction</h3>
                  </div>
                  <span>Revision {inspection.revision}</span>
                </div>

                {operationOptions.length > 1 && (
                  <div
                    className="value-inspector__operation"
                    role="group"
                    aria-label="Correction action"
                  >
                    {operationOptions.map((option) => (
                      <Button variant="ghost"
                        key={option.operation}
                        type="button"
                        disabled={isMutationBlocked}
                        aria-pressed={operation === option.operation}
                        onClick={() => chooseOperation(option.operation)}
                      >
                        {option.operation === "revert" && (
                          <RotateCcw size={14} aria-hidden="true" />
                        )}
                        {option.label}
                      </Button>
                    ))}
                  </div>
                )}

                {operation === "set" && (
                  <label className="value-inspector__field">
                    <span>Corrected value</span>
                    <div className="value-inspector__value-input">
                      <input
                        ref={valueInputRef}
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        aria-label="Corrected value"
                        disabled={isMutationBlocked}
                        value={value}
                        onChange={(event) => {
                          setValue(event.target.value);
                          setValueEdited(true);
                        }}
                        aria-invalid={Boolean(
                          formError?.startsWith("Enter") ||
                          formError?.startsWith("Value") ||
                          formError?.startsWith("Hours"),
                        )}
                      />
                      <span>{displayUnitLabel}</span>
                    </div>
                    {target.displayUnit === "days" &&
                      typeof target.hoursPerDay === "number" &&
                      value.trim() !== "" &&
                      Number.isFinite(Number(value)) && (
                        <small>
                          Sends{" "}
                          {correctionDraftValueHours({
                            value,
                            unit: target.displayUnit,
                            hoursPerDay: target.hoursPerDay,
                            canonicalValueHours:
                              inspection.effectiveValueHours ??
                              inspection.originalValueHours,
                            valueEdited,
                          }).toFixed(2)}{" "}
                          canonical hours
                          {valueEdited
                            ? ` using ${target.hoursPerDay} h/day.`
                            : " from the untouched source value."}
                        </small>
                      )}
                  </label>
                )}

                <label className="value-inspector__field">
                  <span>Reason</span>
                  <select
                    aria-label="Reason"
                    required
                    disabled={isMutationBlocked}
                    value={reasonCategory}
                    onChange={(event) =>
                      setReasonCategory(event.target.value as ReasonCategory)
                    }
                  >
                    {allowedSuggestions.map((suggestion) => (
                      <option key={suggestion.value} value={suggestion.value}>
                        {suggestion.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="value-inspector__field">
                  <span>Note</span>
                  <textarea
                    aria-label="Note"
                    rows={4}
                    required
                    minLength={3}
                    maxLength={500}
                    disabled={isMutationBlocked}
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    placeholder="Record what changed and why."
                    aria-invalid={Boolean(formError?.startsWith("Note"))}
                  />
                  <small className="value-inspector__field-meta">
                    <span>Required · 3–500 characters</span>
                    <span>{note.trim().length}/500</span>
                  </small>
                </label>

                {formError && (
                  <p className="value-inspector__form-error" role="alert">
                    {formError}
                  </p>
                )}

                {conflict && (
                  <div
                    className="value-inspector__state value-inspector__state--conflict"
                    role="alert"
                    aria-labelledby={conflictHeadingId}
                  >
                    <RefreshCw size={17} aria-hidden="true" />
                    <div>
                      <h3 id={conflictHeadingId}>Review the latest value</h3>
                      <p>{conflict}</p>
                      <Button variant="ghost"
                        type="button"
                        disabled={inspectionQuery.isFetching}
                        onClick={reloadAfterConflict}
                      >
                        {inspectionQuery.isFetching
                          ? "Reloading value…"
                          : "Reload value"}
                      </Button>
                    </div>
                  </div>
                )}

                {correctionMutation.isError && !conflict && (
                  <p className="value-inspector__form-error" role="alert">
                    {correctionMutation.error instanceof Error
                      ? correctionMutation.error.message
                      : "The correction could not be saved."}
                  </p>
                )}

                <Button variant="ghost"
                  type="button"
                  className={`value-inspector__primary${operation === "revert" ? " value-inspector__primary--danger" : ""}`}
                  disabled={isMutationBlocked || correctionMutation.isPending}
                  onClick={submit}
                >
                  {correctionMutation.isPending && (
                    <LoaderCircle
                      className="animate-spin"
                      size={16}
                      aria-hidden="true"
                    />
                  )}
                  {correctionMutation.isPending
                    ? "Recording action…"
                    : primaryLabel}
                </Button>
              </section>

              {recalculation && (
                <section
                  className={`value-inspector__recalculation value-inspector__recalculation--${recalculation.state}`}
                  aria-live="polite"
                >
                  {recalculation.state === "complete" ? (
                    <CheckCircle2 size={18} aria-hidden="true" />
                  ) : recalculation.state === "pending" ? (
                    <Clock3 size={18} aria-hidden="true" />
                  ) : (
                    <CircleAlert size={18} aria-hidden="true" />
                  )}
                  <div>
                    <h3>
                      {correctionRecalculationHeading(recalculation.state)}
                    </h3>
                    <p>
                      {recalculation.state === "complete"
                        ? `${recalculation.recalculatedMonths.length} affected month(s) were recalculated.`
                        : "Displayed payroll calculations have not been presented as refreshed."}
                    </p>
                    {[
                      ...recalculation.pendingMonths,
                      ...recalculation.failedMonths,
                    ].length > 0 && (
                      <ul>
                        {[
                          ...recalculation.pendingMonths,
                          ...recalculation.failedMonths,
                        ].map((issue) => (
                          <li key={`${issue.month}:${issue.code}`}>
                            {displayMonth(issue.month)}: {issue.message}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </section>
              )}

              <section
                className="value-inspector__history"
                aria-labelledby={`${headingId}-history`}
              >
                <div className="value-inspector__section-heading">
                  <div>
                    <p className="value-inspector__eyebrow">Change history</p>
                    <h3 id={`${headingId}-history`}>
                      Manual changes to this value
                    </h3>
                  </div>
                  <span>
                    {inspection.events.length}{" "}
                    {inspection.events.length === 1 ? "change" : "changes"}
                  </span>
                </div>
                <ValueCorrectionTimeline
                  events={inspection.events}
                  displayUnit={target.displayUnit}
                  hoursPerDay={target.hoursPerDay}
                />
              </section>
            </>
          )}
        </div>
      </aside>
    </div>
  );

  const portalTarget = valueCorrectionPortalTarget(target.trigger);
  return portalTarget ? createPortal(result, portalTarget) : null;
};
